import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Shield,
  LogOut,
  Plus,
  Pencil,
  Trash2,
  FileText,
  Menu,
  X,
  Eye,
  EyeOff,
  Trophy,
  Save,
  Calendar,
  Upload,
  Image,
  Users,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("articles");
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [formData, setFormData] = useState({
    title: "",
    excerpt: "",
    content: "",
    image_url: "",
    category: "news",
    published_date: new Date().toISOString().split('T')[0], // Data odierna di default
    author: "",
    pdf_url: "",
    pdf_name: "",
  });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadingPdf, setUploadingPdf] = useState(false);
  
  // State per ultimo risultato
  const [lastResult, setLastResult] = useState({
    home_team: "Volpianopianese",
    away_team: "",
    home_score: 0,
    away_score: 0,
    date: "",
    competition: "Eccellenza Piemonte Girone A",
  });
  const [savingResult, setSavingResult] = useState(false);
  const [loadingResult, setLoadingResult] = useState(true);

  // State per prossima partita
  const [nextMatch, setNextMatch] = useState({
    home_team: "Volpianopianese",
    away_team: "",
    date: "",
    time: "14:30",
    location: "Stadio Comunale",
    competition: "Eccellenza Piemonte Girone A",
  });
  const [savingNextMatch, setSavingNextMatch] = useState(false);
  const [loadingNextMatch, setLoadingNextMatch] = useState(true);

  // State per giocatori
  const [players, setPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);
  const [isPlayerDialogOpen, setIsPlayerDialogOpen] = useState(false);
  const [isDeletePlayerDialogOpen, setIsDeletePlayerDialogOpen] = useState(false);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [playerFormData, setPlayerFormData] = useState({
    name: "",
    surname: "",
    number: "",
    position: "",
    image_url: "",
    team: "prima_squadra",
    birth_year: "",
  });
  const [savingPlayer, setSavingPlayer] = useState(false);
  const [uploadingPlayer, setUploadingPlayer] = useState(false);

  const token = localStorage.getItem("admin_token");

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    verifyToken();
    fetchArticles();
    fetchLastResult();
    fetchNextMatch();
    fetchPlayers();
  }, [token, navigate]);

  const verifyToken = async () => {
    try {
      await axios.get(`${API}/admin/verify`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch (error) {
      localStorage.removeItem("admin_token");
      localStorage.removeItem("admin_username");
      navigate("/admin");
    }
  };

  const fetchArticles = async () => {
    try {
      const response = await axios.get(`${API}/admin/articles`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setArticles(response.data);
    } catch (error) {
      console.error("Error fetching articles:", error);
      toast.error("Errore nel caricamento degli articoli");
    } finally {
      setLoading(false);
    }
  };

  const fetchLastResult = async () => {
    try {
      const response = await axios.get(`${API}/last-result`);
      if (response.data) {
        setLastResult(response.data);
      }
    } catch (error) {
      console.error("Error fetching last result:", error);
    } finally {
      setLoadingResult(false);
    }
  };

  const fetchNextMatch = async () => {
    try {
      const response = await axios.get(`${API}/next-match`);
      if (response.data) {
        setNextMatch(response.data);
      }
    } catch (error) {
      console.error("Error fetching next match:", error);
    } finally {
      setLoadingNextMatch(false);
    }
  };

  const saveLastResult = async () => {
    setSavingResult(true);
    try {
      await axios.post(`${API}/admin/last-result`, lastResult, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Ultimo risultato salvato con successo");
    } catch (error) {
      console.error("Error saving last result:", error);
      toast.error("Errore nel salvataggio del risultato");
    } finally {
      setSavingResult(false);
    }
  };

  const saveNextMatch = async () => {
    setSavingNextMatch(true);
    try {
      await axios.post(`${API}/admin/next-match`, nextMatch, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Prossima partita salvata con successo");
    } catch (error) {
      console.error("Error saving next match:", error);
      toast.error("Errore nel salvataggio della partita");
    } finally {
      setSavingNextMatch(false);
    }
  };

  // ============ PLAYERS FUNCTIONS ============
  const fetchPlayers = async () => {
    try {
      const response = await axios.get(`${API}/admin/players`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setPlayers(response.data);
    } catch (error) {
      console.error("Error fetching players:", error);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const openCreatePlayerDialog = () => {
    setSelectedPlayer(null);
    setPlayerFormData({
      name: "",
      surname: "",
      number: "",
      position: "",
      image_url: "",
      team: "prima_squadra",
      birth_year: "",
    });
    setIsPlayerDialogOpen(true);
  };

  const openEditPlayerDialog = (player) => {
    setSelectedPlayer(player);
    setPlayerFormData({
      name: player.name || "",
      surname: player.surname || "",
      number: player.number?.toString() || "",
      position: player.position || "",
      image_url: player.image_url || "",
      team: player.team || "prima_squadra",
      birth_year: player.birth_year?.toString() || "",
    });
    setIsPlayerDialogOpen(true);
  };

  const openDeletePlayerDialog = (player) => {
    setSelectedPlayer(player);
    setIsDeletePlayerDialogOpen(true);
  };

  const handlePlayerImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato non supportato. Usa JPG, PNG, GIF o WebP");
      return;
    }

    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande. Massimo 10MB");
      return;
    }

    setUploadingPlayer(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      const imageUrl = `${BACKEND_URL}${response.data.url}`;
      setPlayerFormData((prev) => ({ ...prev, image_url: imageUrl }));
      toast.success("Immagine caricata con successo!");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Errore nel caricamento dell'immagine");
    } finally {
      setUploadingPlayer(false);
    }
  };

  const savePlayer = async () => {
    setSavingPlayer(true);
    try {
      const dataToSend = {
        ...playerFormData,
        number: playerFormData.number ? parseInt(playerFormData.number) : null,
        birth_year: playerFormData.birth_year ? parseInt(playerFormData.birth_year) : null,
      };

      if (selectedPlayer) {
        await axios.put(`${API}/admin/players/${selectedPlayer.id}`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Giocatore aggiornato con successo");
      } else {
        await axios.post(`${API}/admin/players`, dataToSend, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Giocatore creato con successo");
      }
      setIsPlayerDialogOpen(false);
      fetchPlayers();
    } catch (error) {
      console.error("Error saving player:", error);
      toast.error("Errore nel salvataggio del giocatore");
    } finally {
      setSavingPlayer(false);
    }
  };

  const deletePlayer = async () => {
    try {
      await axios.delete(`${API}/admin/players/${selectedPlayer.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Giocatore eliminato");
      setIsDeletePlayerDialogOpen(false);
      fetchPlayers();
    } catch (error) {
      console.error("Error deleting player:", error);
      toast.error("Errore nell'eliminazione del giocatore");
    }
  };

  const getTeamLabel = (team) => {
    const teams = {
      prima_squadra: "Prima Squadra",
      juniores: "Juniores",
      allievi: "Allievi",
      giovanissimi: "Giovanissimi",
      esordienti: "Esordienti",
      pulcini: "Pulcini",
    };
    return teams[team] || team;
  };

  const getPositionLabel = (position) => {
    const positions = {
      portiere: "Portiere",
      difensore: "Difensore",
      centrocampista: "Centrocampista",
      attaccante: "Attaccante",
      allenatore: "Allenatore",
      vice_allenatore: "Vice Allenatore",
      preparatore_portieri: "Preparatore Portieri",
      preparatore_atletico: "Preparatore Atletico",
      massaggiatore: "Massaggiatore",
      collaboratore_tecnico: "Collaboratore Tecnico",
      team_manager: "Team Manager",
      magazziniere: "Magazziniere",
    };
    return positions[position] || position;
  };

  // ============ ARTICLE FUNCTIONS ============
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Verifica tipo file
    const allowedTypes = ["image/jpeg", "image/png", "image/gif", "image/webp"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Formato non supportato. Usa JPG, PNG, GIF o WebP");
      return;
    }

    // Verifica dimensione (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      toast.error("File troppo grande. Massimo 10MB");
      return;
    }

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      // Usa l'URL restituito (può essere Cloudinary o locale)
      const imageUrl = response.data.url.startsWith("http") 
        ? response.data.url 
        : `${BACKEND_URL}${response.data.url}`;
      setFormData((prev) => ({ ...prev, image_url: imageUrl }));
      toast.success("Immagine caricata con successo!");
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error("Errore nel caricamento dell'immagine");
    } finally {
      setUploading(false);
    }
  };

  const handlePdfUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Verifica tipo file
    if (file.type !== "application/pdf") {
      toast.error("Formato non supportato. Usa solo file PDF");
      return;
    }

    // Verifica dimensione (max 20MB per PDF)
    if (file.size > 20 * 1024 * 1024) {
      toast.error("File troppo grande. Massimo 20MB");
      return;
    }

    setUploadingPdf(true);
    const formDataUpload = new FormData();
    formDataUpload.append("file", file);

    try {
      const response = await axios.post(`${API}/admin/upload-pdf`, formDataUpload, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      
      // Usa l'URL restituito (può essere Cloudinary o locale)
      const pdfUrl = response.data.url.startsWith("http") 
        ? response.data.url 
        : `${BACKEND_URL}${response.data.url}`;
      setFormData((prev) => ({ 
        ...prev, 
        pdf_url: pdfUrl,
        pdf_name: response.data.filename 
      }));
      toast.success("PDF caricato con successo!");
    } catch (error) {
      console.error("Error uploading PDF:", error);
      toast.error("Errore nel caricamento del PDF");
    } finally {
      setUploadingPdf(false);
    }
  };

  const handleRemovePdf = () => {
    setFormData((prev) => ({ ...prev, pdf_url: "", pdf_name: "" }));
  };

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    localStorage.removeItem("admin_username");
    navigate("/admin");
  };

  const openCreateDialog = () => {
    setSelectedArticle(null);
    setFormData({
      title: "",
      excerpt: "",
      content: "",
      image_url: "",
      category: "news",
      published_date: new Date().toISOString().split('T')[0],
      author: "",
      pdf_url: "",
      pdf_name: "",
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (article) => {
    setSelectedArticle(article);
    setFormData({
      title: article.title,
      excerpt: article.excerpt,
      content: article.content,
      image_url: article.image_url || "",
      category: article.category,
      published_date: article.published_date || article.created_at?.split('T')[0] || new Date().toISOString().split('T')[0],
      author: article.author || "",
      pdf_url: article.pdf_url || "",
      pdf_name: article.pdf_name || "",
    });
    setIsDialogOpen(true);
  };

  const openDeleteDialog = (article) => {
    setSelectedArticle(article);
    setIsDeleteDialogOpen(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (selectedArticle) {
        await axios.put(
          `${API}/admin/articles/${selectedArticle.id}`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success("Articolo aggiornato con successo");
      } else {
        await axios.post(`${API}/admin/articles`, formData, {
          headers: { Authorization: `Bearer ${token}` },
        });
        toast.success("Articolo creato con successo");
      }
      setIsDialogOpen(false);
      fetchArticles();
    } catch (error) {
      console.error("Error saving article:", error);
      toast.error("Errore nel salvataggio dell'articolo");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/admin/articles/${selectedArticle.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success("Articolo eliminato con successo");
      setIsDeleteDialogOpen(false);
      fetchArticles();
    } catch (error) {
      console.error("Error deleting article:", error);
      toast.error("Errore nell'eliminazione dell'articolo");
    }
  };

  const togglePublished = async (article) => {
    try {
      await axios.put(
        `${API}/admin/articles/${article.id}`,
        { published: !article.published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(
        article.published
          ? "Articolo nascosto"
          : "Articolo pubblicato"
      );
      fetchArticles();
    } catch (error) {
      console.error("Error toggling published:", error);
      toast.error("Errore nell'aggiornamento");
    }
  };

  const getCategoryLabel = (category) => {
    switch (category) {
      case "partite":
        return "Partite";
      case "comunicati":
        return "Comunicato";
      case "scuola_calcio":
        return "Scuola Calcio";
      default:
        return "News";
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-slate-100">
      {/* Mobile Sidebar Toggle */}
      <button
        data-testid="sidebar-toggle"
        onClick={() => setSidebarOpen(!sidebarOpen)}
        className="lg:hidden fixed top-4 left-4 z-50 p-2 bg-[#0F172A] text-white rounded-lg"
      >
        {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sidebar */}
      <aside
        className={`admin-sidebar ${sidebarOpen ? "open" : ""}`}
        data-testid="admin-sidebar"
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_volpiano-calcio/artifacts/8nz2bql1_IMG_8381.webp" 
              alt="G.S.D. Volpiano Pianese"
              className="h-12 w-auto bg-white rounded-lg p-1"
            />
            <div>
              <span className="text-white font-bold">Volpianopianese</span>
              <span className="block text-slate-400 text-xs">Admin Panel</span>
            </div>
          </div>

          <nav className="space-y-2">
            <button
              onClick={() => setActiveTab("articles")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "articles"
                  ? "bg-[#0047AB] text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
              data-testid="nav-articles"
            >
              <FileText className="w-5 h-5" />
              Articoli
            </button>
            <button
              onClick={() => setActiveTab("lastResult")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "lastResult"
                  ? "bg-[#0047AB] text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
              data-testid="nav-last-result"
            >
              <Trophy className="w-5 h-5" />
              Ultimo Risultato
            </button>
            <button
              onClick={() => setActiveTab("nextMatch")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "nextMatch"
                  ? "bg-[#0047AB] text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
              data-testid="nav-next-match"
            >
              <Calendar className="w-5 h-5" />
              Prossima Partita
            </button>
            <button
              onClick={() => setActiveTab("players")}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === "players"
                  ? "bg-[#0047AB] text-white"
                  : "text-slate-400 hover:text-white hover:bg-slate-800"
              }`}
              data-testid="nav-players"
            >
              <Users className="w-5 h-5" />
              Giocatori
            </button>
          </nav>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-6 border-t border-slate-800">
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-slate-400 hover:text-white transition-colors"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
            Esci
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-content">
        <div className="p-6 lg:p-8">
          {activeTab === "articles" ? (
            <>
              {/* Header Articoli */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Articoli</h1>
                  <p className="text-slate-500">
                    Gestisci le news e i comunicati del sito
                  </p>
                </div>
                <Button
                  onClick={openCreateDialog}
                  className="bg-[#0047AB] hover:bg-[#003380]"
                  data-testid="create-article-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Articolo
                </Button>
              </div>

              {/* Articles Table */}
              {loading ? (
                <div className="bg-white rounded-xl p-8">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-16 bg-slate-100 rounded-lg" />
                    ))}
                  </div>
                </div>
              ) : articles.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table
                      className="w-full"
                      data-testid="articles-table"
                    >
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">
                            Titolo
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">
                            Categoria
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">
                            Stato
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">
                            Data
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">
                            Azioni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {articles.map((article) => (
                          <tr
                            key={article.id}
                            data-testid={`article-row-${article.id}`}
                            className="hover:bg-slate-50"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                {article.image_url && (
                                  <img
                                    src={article.image_url}
                                    alt=""
                                    className="w-10 h-10 rounded-lg object-cover"
                                  />
                                )}
                                <span className="font-medium text-slate-900 line-clamp-1">
                                  {article.title}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-[#0047AB]">
                                {getCategoryLabel(article.category)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <button
                                onClick={() => togglePublished(article)}
                                data-testid={`toggle-published-${article.id}`}
                                className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded-full ${
                                  article.published
                                    ? "bg-green-50 text-green-600"
                                    : "bg-slate-100 text-slate-500"
                                }`}
                              >
                                {article.published ? (
                                  <>
                                    <Eye className="w-3 h-3" /> Pubblicato
                                  </>
                                ) : (
                                  <>
                                    <EyeOff className="w-3 h-3" /> Nascosto
                                  </>
                                )}
                              </button>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-500">
                              {new Date(article.published_date || article.created_at).toLocaleDateString(
                                "it-IT"
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => openEditDialog(article)}
                                  data-testid={`edit-article-${article.id}`}
                                  className="p-2 text-slate-500 hover:text-[#0047AB] hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => openDeleteDialog(article)}
                                  data-testid={`delete-article-${article.id}`}
                                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-12 text-center">
                  <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">
                    Nessun articolo ancora. Crea il primo!
                  </p>
                  <Button
                    onClick={openCreateDialog}
                    className="bg-[#0047AB] hover:bg-[#003380]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Articolo
                  </Button>
                </div>
              )}
            </>
          ) : activeTab === "lastResult" ? (
            <>
              {/* Header Ultimo Risultato */}
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-slate-900">Ultimo Risultato</h1>
                <p className="text-slate-500">
                  Gestisci l'ultimo risultato mostrato in homepage
                </p>
              </div>

              {/* Form Ultimo Risultato */}
              {loadingResult ? (
                <div className="bg-white rounded-xl p-8">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-12 bg-slate-100 rounded-lg" />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm p-6" data-testid="last-result-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Squadra Casa *
                      </label>
                      <Input
                        value={lastResult.home_team}
                        onChange={(e) =>
                          setLastResult((prev) => ({ ...prev, home_team: e.target.value }))
                        }
                        placeholder="Es: Volpianopianese"
                        data-testid="result-home-team"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Squadra Ospite *
                      </label>
                      <Input
                        value={lastResult.away_team}
                        onChange={(e) =>
                          setLastResult((prev) => ({ ...prev, away_team: e.target.value }))
                        }
                        placeholder="Es: Borgosesia"
                        data-testid="result-away-team"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Gol Casa *
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={lastResult.home_score}
                        onChange={(e) =>
                          setLastResult((prev) => ({ ...prev, home_score: parseInt(e.target.value) || 0 }))
                        }
                        data-testid="result-home-score"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Gol Ospite *
                      </label>
                      <Input
                        type="number"
                        min="0"
                        value={lastResult.away_score}
                        onChange={(e) =>
                          setLastResult((prev) => ({ ...prev, away_score: parseInt(e.target.value) || 0 }))
                        }
                        data-testid="result-away-score"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Data Partita *
                      </label>
                      <Input
                        type="date"
                        value={lastResult.date}
                        onChange={(e) =>
                          setLastResult((prev) => ({ ...prev, date: e.target.value }))
                        }
                        data-testid="result-date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Competizione
                      </label>
                      <Input
                        value={lastResult.competition}
                        onChange={(e) =>
                          setLastResult((prev) => ({ ...prev, competition: e.target.value }))
                        }
                        placeholder="Es: Eccellenza Piemonte Girone A"
                        data-testid="result-competition"
                      />
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="mt-8 pt-6 border-t">
                    <h3 className="text-sm font-medium text-slate-500 mb-4">Anteprima</h3>
                    <div className="bg-slate-50 rounded-xl p-6 max-w-md mx-auto">
                      <div className="text-center space-y-3">
                        <div className="font-bold text-lg text-slate-800">
                          {lastResult.home_team || "Squadra Casa"}
                        </div>
                        <div className="bg-[#0F172A] text-white py-3 px-6 rounded-xl inline-block">
                          <span className="font-bold text-3xl">
                            {lastResult.home_score} - {lastResult.away_score}
                          </span>
                        </div>
                        <div className="font-bold text-lg text-slate-800">
                          {lastResult.away_team || "Squadra Ospite"}
                        </div>
                        {lastResult.date && (
                          <p className="text-slate-500 text-sm">
                            {new Date(lastResult.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button
                      onClick={saveLastResult}
                      disabled={savingResult || !lastResult.home_team || !lastResult.away_team || !lastResult.date}
                      className="bg-[#0047AB] hover:bg-[#003380]"
                      data-testid="save-result-btn"
                    >
                      {savingResult ? (
                        "Salvataggio..."
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Salva Risultato
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : activeTab === "nextMatch" ? (
            <>
              {/* Header Prossima Partita */}
              <div className="mb-8">
                <h1 className="text-2xl font-bold text-slate-900">Prossima Partita</h1>
                <p className="text-slate-500">
                  Gestisci la prossima partita mostrata in homepage
                </p>
              </div>

              {/* Form Prossima Partita */}
              {loadingNextMatch ? (
                <div className="bg-white rounded-xl p-8">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3].map((i) => (
                      <div key={i} className="h-12 bg-slate-100 rounded-lg" />
                    ))}
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl shadow-sm p-6" data-testid="next-match-form">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Squadra Casa *
                      </label>
                      <Input
                        value={nextMatch.home_team}
                        onChange={(e) =>
                          setNextMatch((prev) => ({ ...prev, home_team: e.target.value }))
                        }
                        placeholder="Es: Volpianopianese"
                        data-testid="next-home-team"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Squadra Ospite *
                      </label>
                      <Input
                        value={nextMatch.away_team}
                        onChange={(e) =>
                          setNextMatch((prev) => ({ ...prev, away_team: e.target.value }))
                        }
                        placeholder="Es: Borgosesia"
                        data-testid="next-away-team"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Data Partita *
                      </label>
                      <Input
                        type="date"
                        value={nextMatch.date}
                        onChange={(e) =>
                          setNextMatch((prev) => ({ ...prev, date: e.target.value }))
                        }
                        data-testid="next-date"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Orario
                      </label>
                      <Input
                        type="time"
                        value={nextMatch.time}
                        onChange={(e) =>
                          setNextMatch((prev) => ({ ...prev, time: e.target.value }))
                        }
                        data-testid="next-time"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Stadio/Località
                      </label>
                      <Input
                        value={nextMatch.location}
                        onChange={(e) =>
                          setNextMatch((prev) => ({ ...prev, location: e.target.value }))
                        }
                        placeholder="Es: Stadio Comunale"
                        data-testid="next-location"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-2">
                        Competizione
                      </label>
                      <Input
                        value={nextMatch.competition}
                        onChange={(e) =>
                          setNextMatch((prev) => ({ ...prev, competition: e.target.value }))
                        }
                        placeholder="Es: Eccellenza Piemonte Girone A"
                        data-testid="next-competition"
                      />
                    </div>
                  </div>

                  {/* Preview */}
                  <div className="mt-8 pt-6 border-t">
                    <h3 className="text-sm font-medium text-slate-500 mb-4">Anteprima</h3>
                    <div className="bg-slate-50 rounded-xl p-6 max-w-md mx-auto">
                      <div className="text-center space-y-3">
                        <span className="inline-block text-xs font-semibold px-3 py-1 bg-blue-100 text-[#0047AB] rounded-full">
                          PROSSIMA PARTITA
                        </span>
                        <div className="font-bold text-lg text-slate-800">
                          {nextMatch.home_team || "Squadra Casa"}
                        </div>
                        <div className="text-slate-400 text-xl font-bold">VS</div>
                        <div className="font-bold text-lg text-slate-800">
                          {nextMatch.away_team || "Squadra Ospite"}
                        </div>
                        {nextMatch.date && (
                          <p className="text-slate-500 text-sm">
                            {new Date(nextMatch.date).toLocaleDateString('it-IT', { weekday: 'long', day: 'numeric', month: 'long' })}
                            {nextMatch.time && ` - ${nextMatch.time}`}
                          </p>
                        )}
                        {nextMatch.location && (
                          <p className="text-slate-400 text-xs">{nextMatch.location}</p>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="mt-6 flex justify-end">
                    <Button
                      onClick={saveNextMatch}
                      disabled={savingNextMatch || !nextMatch.home_team || !nextMatch.away_team || !nextMatch.date}
                      className="bg-[#0047AB] hover:bg-[#003380]"
                      data-testid="save-next-match-btn"
                    >
                      {savingNextMatch ? (
                        "Salvataggio..."
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Salva Partita
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : activeTab === "players" ? (
            <>
              {/* Header Giocatori */}
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
                <div>
                  <h1 className="text-2xl font-bold text-slate-900">Giocatori</h1>
                  <p className="text-slate-500">
                    Gestisci i giocatori delle squadre
                  </p>
                </div>
                <Button
                  onClick={openCreatePlayerDialog}
                  className="bg-[#0047AB] hover:bg-[#003380]"
                  data-testid="create-player-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Nuovo Giocatore
                </Button>
              </div>

              {/* Players Table */}
              {loadingPlayers ? (
                <div className="bg-white rounded-xl p-8">
                  <div className="animate-pulse space-y-4">
                    {[1, 2, 3, 4].map((i) => (
                      <div key={i} className="h-16 bg-slate-100 rounded-lg" />
                    ))}
                  </div>
                </div>
              ) : players.length > 0 ? (
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="overflow-x-auto">
                    <table className="w-full" data-testid="players-table">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">
                            Giocatore
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">
                            Ruolo
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">
                            N°
                          </th>
                          <th className="px-6 py-4 text-left text-xs font-semibold text-slate-500 uppercase">
                            Squadra
                          </th>
                          <th className="px-6 py-4 text-right text-xs font-semibold text-slate-500 uppercase">
                            Azioni
                          </th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {players.map((player) => (
                          <tr
                            key={player.id}
                            data-testid={`player-row-${player.id}`}
                            className="hover:bg-slate-50"
                          >
                            <td className="px-6 py-4">
                              <div className="flex items-center gap-3">
                                {player.image_url ? (
                                  <img
                                    src={player.image_url}
                                    alt=""
                                    className="w-10 h-10 rounded-full object-cover bg-slate-100"
                                  />
                                ) : (
                                  <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                                    <Users className="w-5 h-5 text-slate-400" />
                                  </div>
                                )}
                                <span className="font-medium text-slate-900">
                                  {player.name} {player.surname}
                                </span>
                              </div>
                            </td>
                            <td className="px-6 py-4 text-sm text-slate-600">
                              {player.position ? getPositionLabel(player.position) : "-"}
                            </td>
                            <td className="px-6 py-4">
                              {player.number ? (
                                <span className="inline-flex items-center justify-center w-8 h-8 bg-[#0047AB] text-white text-sm font-bold rounded-full">
                                  {player.number}
                                </span>
                              ) : (
                                <span className="text-slate-400">-</span>
                              )}
                            </td>
                            <td className="px-6 py-4">
                              <span className="text-xs font-medium px-2 py-1 rounded-full bg-blue-50 text-[#0047AB]">
                                {getTeamLabel(player.team)}
                              </span>
                            </td>
                            <td className="px-6 py-4">
                              <div className="flex items-center justify-end gap-2">
                                <button
                                  onClick={() => openEditPlayerDialog(player)}
                                  data-testid={`edit-player-${player.id}`}
                                  className="p-2 text-slate-500 hover:text-[#0047AB] hover:bg-blue-50 rounded-lg transition-colors"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                                <button
                                  onClick={() => openDeletePlayerDialog(player)}
                                  data-testid={`delete-player-${player.id}`}
                                  className="p-2 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-xl p-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500 mb-4">
                    Nessun giocatore ancora. Aggiungi il primo!
                  </p>
                  <Button
                    onClick={openCreatePlayerDialog}
                    className="bg-[#0047AB] hover:bg-[#003380]"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Nuovo Giocatore
                  </Button>
                </div>
              )}
            </>
          ) : null}
        </div>
      </main>

      {/* Create/Edit Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="article-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedArticle ? "Modifica Articolo" : "Nuovo Articolo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Titolo *
              </label>
              <Input
                value={formData.title}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Titolo dell'articolo"
                data-testid="article-title-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Categoria
              </label>
              <Select
                value={formData.category}
                onValueChange={(value) =>
                  setFormData((prev) => ({ ...prev, category: value }))
                }
              >
                <SelectTrigger data-testid="article-category-select">
                  <SelectValue placeholder="Seleziona categoria" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="news">News</SelectItem>
                  <SelectItem value="partite">Partite</SelectItem>
                  <SelectItem value="comunicati">Comunicati Ufficiali</SelectItem>
                  <SelectItem value="scuola_calcio">Programma Scuola Calcio</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Data Pubblicazione
              </label>
              <Input
                type="date"
                value={formData.published_date}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, published_date: e.target.value }))
                }
                data-testid="article-date-input"
              />
              <p className="text-xs text-slate-400 mt-1">
                Puoi retrodatare l'articolo selezionando una data passata
              </p>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Autore (opzionale)
              </label>
              <Input
                type="text"
                placeholder="Nome dell'autore"
                value={formData.author}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, author: e.target.value }))
                }
                data-testid="article-author-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Immagine
              </label>
              <div className="space-y-3">
                {/* Upload da locale */}
                <div className="flex items-center gap-3">
                  <label className="flex-1">
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${uploading ? 'border-blue-300 bg-blue-50' : 'border-slate-300 hover:border-[#0047AB] hover:bg-blue-50'}`}>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handleImageUpload}
                        className="hidden"
                        disabled={uploading}
                        data-testid="article-image-upload"
                      />
                      <div className="flex flex-col items-center gap-2">
                        {uploading ? (
                          <>
                            <div className="w-6 h-6 border-2 border-[#0047AB] border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-[#0047AB]">Caricamento...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-slate-400" />
                            <span className="text-sm text-slate-500">Clicca per caricare un'immagine</span>
                            <span className="text-xs text-slate-400">JPG, PNG, GIF, WebP (max 10MB)</span>
                          </>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
                
                {/* Oppure URL manuale */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400">oppure inserisci URL</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                
                <Input
                  value={formData.image_url}
                  onChange={(e) =>
                    setFormData((prev) => ({ ...prev, image_url: e.target.value }))
                  }
                  placeholder="https://..."
                  data-testid="article-image-input"
                />
                
                {/* Preview immagine */}
                {formData.image_url && (
                  <div className="relative">
                    <div className="aspect-video w-full rounded-lg overflow-hidden bg-slate-100">
                      <img
                        src={formData.image_url}
                        alt="Anteprima"
                        className="w-full h-full object-cover"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setFormData((prev) => ({ ...prev, image_url: "" }))}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Upload PDF */}
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Allegato PDF (opzionale)
              </label>
              <div className="space-y-3">
                {formData.pdf_url ? (
                  <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2">
                      <FileText className="w-5 h-5 text-green-600" />
                      <span className="text-sm text-green-800 font-medium">{formData.pdf_name || "PDF allegato"}</span>
                    </div>
                    <button
                      type="button"
                      onClick={handleRemovePdf}
                      className="p-1.5 text-red-500 hover:bg-red-100 rounded-full transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <label className="block">
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${uploadingPdf ? 'border-green-300 bg-green-50' : 'border-slate-300 hover:border-green-500 hover:bg-green-50'}`}>
                      <input
                        type="file"
                        accept="application/pdf"
                        onChange={handlePdfUpload}
                        className="hidden"
                        disabled={uploadingPdf}
                        data-testid="article-pdf-upload"
                      />
                      <div className="flex flex-col items-center gap-2">
                        {uploadingPdf ? (
                          <>
                            <div className="w-6 h-6 border-2 border-green-500 border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-green-600">Caricamento PDF...</span>
                          </>
                        ) : (
                          <>
                            <FileText className="w-6 h-6 text-slate-400" />
                            <span className="text-sm text-slate-500">Clicca per caricare un PDF</span>
                            <span className="text-xs text-slate-400">Solo file PDF (max 20MB)</span>
                          </>
                        )}
                      </div>
                    </div>
                  </label>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Sommario *
              </label>
              <Textarea
                value={formData.excerpt}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, excerpt: e.target.value }))
                }
                placeholder="Breve descrizione dell'articolo"
                rows={2}
                data-testid="article-excerpt-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Contenuto *
              </label>
              <Textarea
                value={formData.content}
                onChange={(e) =>
                  setFormData((prev) => ({ ...prev, content: e.target.value }))
                }
                placeholder="Contenuto completo dell'articolo"
                rows={6}
                data-testid="article-content-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsDialogOpen(false)}
              data-testid="cancel-article-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={handleSave}
              disabled={saving || !formData.title || !formData.excerpt || !formData.content}
              className="bg-[#0047AB] hover:bg-[#003380]"
              data-testid="save-article-btn"
            >
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent data-testid="delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Conferma eliminazione</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare l'articolo "{selectedArticle?.title}"?
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-btn">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Create/Edit Player Dialog */}
      <Dialog open={isPlayerDialogOpen} onOpenChange={setIsPlayerDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="player-dialog">
          <DialogHeader>
            <DialogTitle>
              {selectedPlayer ? "Modifica Giocatore" : "Nuovo Giocatore"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Nome
                </label>
                <Input
                  value={playerFormData.name}
                  onChange={(e) =>
                    setPlayerFormData((prev) => ({ ...prev, name: e.target.value }))
                  }
                  placeholder="Nome"
                  data-testid="player-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Cognome
                </label>
                <Input
                  value={playerFormData.surname}
                  onChange={(e) =>
                    setPlayerFormData((prev) => ({ ...prev, surname: e.target.value }))
                  }
                  placeholder="Cognome"
                  data-testid="player-surname-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Numero di Maglia
                </label>
                <Input
                  type="number"
                  min="1"
                  max="99"
                  value={playerFormData.number}
                  onChange={(e) =>
                    setPlayerFormData((prev) => ({ ...prev, number: e.target.value }))
                  }
                  placeholder="Es: 10"
                  data-testid="player-number-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Anno di Nascita
                </label>
                <Input
                  type="number"
                  min="1950"
                  max="2025"
                  value={playerFormData.birth_year}
                  onChange={(e) =>
                    setPlayerFormData((prev) => ({ ...prev, birth_year: e.target.value }))
                  }
                  placeholder="Es: 1998"
                  data-testid="player-birthyear-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Ruolo
                </label>
                <Select
                  value={playerFormData.position}
                  onValueChange={(value) =>
                    setPlayerFormData((prev) => ({ ...prev, position: value }))
                  }
                >
                  <SelectTrigger data-testid="player-position-select">
                    <SelectValue placeholder="Seleziona ruolo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="portiere">Portiere</SelectItem>
                    <SelectItem value="difensore">Difensore</SelectItem>
                    <SelectItem value="centrocampista">Centrocampista</SelectItem>
                    <SelectItem value="attaccante">Attaccante</SelectItem>
                    <SelectItem value="allenatore">Allenatore</SelectItem>
                    <SelectItem value="vice_allenatore">Vice Allenatore</SelectItem>
                    <SelectItem value="preparatore_portieri">Preparatore Portieri</SelectItem>
                    <SelectItem value="preparatore_atletico">Preparatore Atletico</SelectItem>
                    <SelectItem value="massaggiatore">Massaggiatore</SelectItem>
                    <SelectItem value="collaboratore_tecnico">Collaboratore Tecnico</SelectItem>
                    <SelectItem value="team_manager">Team Manager</SelectItem>
                    <SelectItem value="magazziniere">Magazziniere</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  Squadra
                </label>
                <Select
                  value={playerFormData.team}
                  onValueChange={(value) =>
                    setPlayerFormData((prev) => ({ ...prev, team: value }))
                  }
                >
                  <SelectTrigger data-testid="player-team-select">
                    <SelectValue placeholder="Seleziona squadra" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="prima_squadra">Prima Squadra</SelectItem>
                    <SelectItem value="juniores">Juniores</SelectItem>
                    <SelectItem value="allievi">Allievi</SelectItem>
                    <SelectItem value="giovanissimi">Giovanissimi</SelectItem>
                    <SelectItem value="esordienti">Esordienti</SelectItem>
                    <SelectItem value="pulcini">Pulcini</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Foto Giocatore
              </label>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <label className="flex-1">
                    <div className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${uploadingPlayer ? 'border-blue-300 bg-blue-50' : 'border-slate-300 hover:border-[#0047AB] hover:bg-blue-50'}`}>
                      <input
                        type="file"
                        accept="image/jpeg,image/png,image/gif,image/webp"
                        onChange={handlePlayerImageUpload}
                        className="hidden"
                        disabled={uploadingPlayer}
                        data-testid="player-image-upload"
                      />
                      <div className="flex flex-col items-center gap-2">
                        {uploadingPlayer ? (
                          <>
                            <div className="w-6 h-6 border-2 border-[#0047AB] border-t-transparent rounded-full animate-spin" />
                            <span className="text-sm text-[#0047AB]">Caricamento...</span>
                          </>
                        ) : (
                          <>
                            <Upload className="w-6 h-6 text-slate-400" />
                            <span className="text-sm text-slate-500">Clicca per caricare la foto</span>
                            <span className="text-xs text-slate-400">JPG, PNG, GIF, WebP (max 10MB)</span>
                          </>
                        )}
                      </div>
                    </div>
                  </label>
                </div>
                
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-px bg-slate-200" />
                  <span className="text-xs text-slate-400">oppure inserisci URL</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>
                
                <Input
                  value={playerFormData.image_url}
                  onChange={(e) =>
                    setPlayerFormData((prev) => ({ ...prev, image_url: e.target.value }))
                  }
                  placeholder="https://..."
                  data-testid="player-image-input"
                />
                
                {playerFormData.image_url && (
                  <div className="relative max-w-[200px] mx-auto">
                    <div className="aspect-[3/4] w-full rounded-lg overflow-hidden bg-slate-100">
                      <img
                        src={playerFormData.image_url}
                        alt="Anteprima"
                        className="w-full h-full object-contain"
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => setPlayerFormData((prev) => ({ ...prev, image_url: "" }))}
                      className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsPlayerDialogOpen(false)}
              data-testid="cancel-player-btn"
            >
              Annulla
            </Button>
            <Button
              onClick={savePlayer}
              disabled={savingPlayer}
              className="bg-[#0047AB] hover:bg-[#003380]"
              data-testid="save-player-btn"
            >
              {savingPlayer ? "Salvataggio..." : "Salva"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Player Dialog */}
      <AlertDialog open={isDeletePlayerDialogOpen} onOpenChange={setIsDeletePlayerDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina Giocatore</AlertDialogTitle>
            <AlertDialogDescription>
              Sei sicuro di voler eliminare {selectedPlayer?.name} {selectedPlayer?.surname}?
              Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-player-btn">
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={deletePlayer}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-player-btn"
            >
              Elimina
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default AdminDashboard;
